import {
  DOCS_URLS,
  useHasRole,
  useNavigateWithSearchParams,
  useUser,
} from "@p0security/frontend-core";
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Radio,
  Spacing,
  Typography,
} from "@p0security/ui";
import { startCase } from "lodash";
import React, { useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { spacing } from "styles/spacing";

import { CONFIG_ROUTES_ROLES } from "../../App/roles";
import { AllIntegrationLogos } from "../../Integrations/IntegrationLogo.tsx";
import { Splash } from "../../Splash";
import { Footer } from "../components/Footer";
import { SkipOnboardingButton } from "../components/SkipOnboardingButton";
import { WelcomePaths } from "../paths";
import { SupportedCloudProviders } from "../types";

const CloudProviderForm: React.FC = () => {
  const { orgSlug } = useParams();
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const handleSubmit = useCallback(
    (cloudProvider: { cloudStack: SupportedCloudProviders }) => {
      navigateWithSearchParams(
        `/o/${orgSlug}/${WelcomePaths.Root}/${cloudProvider.cloudStack}/`
      );
    },
    [navigateWithSearchParams, orgSlug]
  );

  return (
    <>
      <Typography.Paragraph>
        To get started, select the cloud provider you&apos;d like to integrate
        with P0 first (you can set up others later on):
      </Typography.Paragraph>

      <Spacing mb={20}>
        <Form
          onFinish={handleSubmit}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
          initialValues={{ cloudStack: "aws" }}
        >
          <Form.Item name="cloudStack">
            <Radio.Group
              /* eslint-disable-line react/forbid-component-props */ style={{
                display: "flex",
                flexDirection: "column",
                gap: spacing.layout.grid.gap.sm,
              }}
              options={[
                {
                  value: "aws",
                  label: (
                    <>{AllIntegrationLogos["aws"]}&nbsp;Amazon Web Services</>
                  ),
                },
                {
                  value: "gcloud",
                  label: (
                    <>
                      {AllIntegrationLogos["gcloud"]}&nbsp;Google Cloud Platform
                    </>
                  ),
                },
                {
                  value: "azure",
                  label: (
                    <>{AllIntegrationLogos["azure"]}&nbsp;Microsoft Azure</>
                  ),
                },
              ]}
            />
          </Form.Item>
          <br />
          <Form.Item className="flex justify-center">
            <Button htmlType="submit" type="primary">
              Submit
            </Button>
          </Form.Item>
          <Divider />
          <Flex justify="center" className="flex-col items-center">
            <SkipOnboardingButton />
          </Flex>
        </Form>
      </Spacing>
    </>
  );
};

const NoAccessMessage: React.FC = () => (
  <Typography.Paragraph>
    It seems you don&apos;t have access to the configuration page. Please
    contact support if you think this is an error.
    <br />
    <br />
    For now, you can get started exploring the app by going to the
    <SkipOnboardingButton textOverride="P0 Dashboard" />
  </Typography.Paragraph>
);

const Welcome = React.memo(() => {
  const { user } = useUser();
  const hasConfigRole = useHasRole(...CONFIG_ROUTES_ROLES);

  const userName = user?.displayName || undefined;

  return (
    <Splash overrideColumn>
      <Col xs={22} md={16} lg={14} xl={12}>
        <Card variant="borderless">
          <>
            <Typography.Title level={4}>
              Welcome to P0 Security
              {userName && `, ${startCase(userName)}`}!
            </Typography.Title>
            <Typography.Paragraph>
              Let&apos;s get set up! For the best onboarding experience, follow
              the steps in our&nbsp;
              <Link to={DOCS_URLS.QUICK_START} target="_blank" rel="noreferrer">
                quick start guide
              </Link>
              .
            </Typography.Paragraph>
          </>

          {hasConfigRole ? <CloudProviderForm /> : <NoAccessMessage />}

          <Footer />
        </Card>
      </Col>
    </Splash>
  );
});

Welcome.displayName = "Welcome";

export { Welcome };
